import {
	Area,
	AreaChart,
	CartesianGrid,
	ReferenceDot,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

export interface TrendChartSeries {
	dataKey: string;
	name?: string;
	// Renders this series in series[0]'s own colour at reduced opacity instead
	// of claiming the next categorical hue — the "current period, paler prior
	// period" overlay pattern (Meta Ads Clicks vs. Previous Period) is ONE
	// metric compared against its own past, not two different metrics, so it
	// reads wrong in two unrelated hues. Every existing single-series caller
	// leaves this unset and is unaffected.
	muted?: boolean;
}

export interface TrendChartProps<T extends object> {
	data: T[];
	series: TrendChartSeries[];
	height?: number;
	xKey: string;
	xTickFormatter?: (value: string) => string;
	showCurrentValue?: boolean;
	// Recharts XAxis `interval` passthrough -- undefined preserves recharts'
	// own default (every tick), which is fine for the short/blanked-tick
	// sparklines this component originally served. A real daily series with
	// visible tick labels (e.g. a 3-week Clicks trend) needs this set
	// explicitly or every one of ~20 ticks renders and collides.
	xAxisInterval?: number | "preserveStart" | "preserveEnd" | "preserveStartEnd";
}

const SERIES_COLORS = [
	{
		stroke: "var(--color-chart-series-1)",
		fill: "color-mix(in srgb, var(--color-chart-series-1) 12%, transparent)",
	},
	{
		stroke: "var(--color-chart-series-2)",
		fill: "color-mix(in srgb, var(--color-chart-series-2) 12%, transparent)",
	},
	{
		stroke: "var(--color-chart-series-3)",
		fill: "color-mix(in srgb, var(--color-chart-series-3) 12%, transparent)",
	},
	{
		stroke: "var(--color-chart-series-4)",
		fill: "color-mix(in srgb, var(--color-chart-series-4) 12%, transparent)",
	},
	{
		stroke: "var(--color-chart-series-5)",
		fill: "color-mix(in srgb, var(--color-chart-series-5) 12%, transparent)",
	},
] as const;

export function TrendChart<T extends object>({
	data,
	series,
	height = 220,
	xKey,
	xTickFormatter,
	showCurrentValue = true,
	xAxisInterval,
}: TrendChartProps<T>) {
	const currentDatum = data.at(-1) as Record<string, unknown> | undefined;
	const currentX = currentDatum?.[xKey];

	return (
		<ResponsiveContainer width="100%" height={height}>
			<AreaChart data={data}>
				<CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
				<XAxis
					dataKey={xKey}
					tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
					tickFormatter={xTickFormatter}
					interval={xAxisInterval}
				/>
				<YAxis tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }} />
				<Tooltip
					formatter={(value, name) => [
						<span className="tabular" key="value">
							{String(value)}
						</span>,
						name,
					]}
					labelFormatter={(label) =>
						xTickFormatter ? xTickFormatter(String(label)) : String(label)
					}
				/>
				{showCurrentValue && currentX != null ? (
					<ReferenceLine
						x={currentX as string | number}
						stroke="var(--color-border)"
						strokeDasharray="3 3"
					/>
				) : null}
				{series.map((s, i) => {
					// `muted` reuses series[0]'s hue (not its own palette slot) at
					// reduced stroke/fill opacity -- see TrendChartSeries.muted doc.
					const color = SERIES_COLORS[s.muted ? 0 : i % SERIES_COLORS.length];
					return (
						<Area
							key={s.dataKey}
							type="monotone"
							dataKey={s.dataKey}
							name={s.name}
							stroke={color.stroke}
							fill={color.fill}
							strokeWidth={2}
							strokeOpacity={s.muted ? 0.4 : 1}
							fillOpacity={s.muted ? 0.4 : 1}
						/>
					);
				})}
				{showCurrentValue && currentX != null
					? series.map((s, i) => {
							const value = currentDatum?.[s.dataKey];
							if (typeof value !== "number") return null;
							const color = SERIES_COLORS[s.muted ? 0 : i % SERIES_COLORS.length];
							return (
								<ReferenceDot
									key={`current-${s.dataKey}`}
									x={currentX as string | number}
									y={value}
									r={4}
									fill={color.stroke}
									stroke="var(--color-paper)"
									strokeWidth={2}
									fillOpacity={s.muted ? 0.4 : 1}
									ifOverflow="extendDomain"
								/>
							);
						})
					: null}
			</AreaChart>
		</ResponsiveContainer>
	);
}
